/*
 * --------------------------------------
 * Copyright (c) 2020 BetterCapital.US
 * --------------------------------------
 */

import { fork, all } from "redux-saga/effects";

import {
  watchLogin,
  watchSignup,
  watchForgotPassword,
  watchResetPassword,
  watchGoogleLogin,
  watchGetUserDetail,
  watchUpdateUserDetail,
  watchActivateUser,
  watchUpdatePassword,
  watchInvestorProfile,
  watchUpdateInvestorProfile,
  watchSaveFilter,
} from "./authSagas";

import {
  watchAssetList,
  watchLookupAsset,
  watchSaveAsset,
  watchAssetDetail,
  watchDeleteAsset,
  watchGetAssetBasicInfo,
  watchUpdateAssetBasicInfo,
  watchGetAssetInvestmentInfo,
  watchUpdateAssetInvestmentInfo,
  watchGetAccountDetail,
  watchGetAssetExpenses,
  watchGetAssetIncomes,
  watchGetAssetValuation,
  watchGetAssetCashflow,
  watchGetMonthwiseIncomes,
  watchGetMonthwiseExpenses,
  watchHasAsset,
  watchGetMonthlyOverallCashflow,
  watchGetTotalMonthlyStats,
  watchGetDashboardStats,
  watchGetAssetStats,
  watchSaveAssetNotFoundLog,
  watchExportToExcel,
  watchGetMonthlyGraph,
  watchGetYearlyGraph,
  watchUpdateIncomeGoal,
  watchGetAssetChecklist,
  watchGetCashFlowGraph,
  watchAddAssetMedias,
  watchGetAssetMedias,
  watchDeleteAssetMedia,
  watchGetMonthlySnapshot,
  watchBulkAddAsset,
  watchDownloadBulkAddCSV,
  watchGetContactAssets,
  watchAddContactAssets,
  watchAddDocument,
  watchSavePartnerTransactions,
  watchDownloadAssetsBulkAddCSV,
  watchBulkAddClient,
  watchBulkAddClientAssets,
  watchManualBulkAddClientAssets,
  watchLookupParcel,
  watchAddAssetAndFinancials,
} from "./assetSagas";

import {
  watchGetAccounts,
  watchAddAccount,
  watchAddBankToAsset,
  watchRemoveBankFromAsset,
  watchRemoveAccount,
  watchUnlinkAssets,
} from "./accountSagas";

import {
  watchGetTransactions,
  watchSaveTransactions,
  watchGetTransactionsFromBank,
  watchUpdateTransaction,
  watchDeleteTransaction,
  watchMultiDeleteTransaction,
  watchDownloadSampleCSV,
  watchRemoveAllDuplicates,
  watchMarkAllPaid,
  watchdownloadPastSampleXLSX,
} from "./transactionSagas";

import { watchResendEmail } from "./resendEmailSaga";

import { watchGetPolicyContent, watchSaveFeedback } from "./cmsSagas";

import {
  watchGetResources,
  watchReserveResource,
  watchGetResourceStats,
  watchPostResource,
  watchUploadFile,
  watchGetMyResources,
  watchGetMyResourceStats,
  watchGetViewResource,
  watchUploadFiles,
} from "./resourceSagas";

import {
  watchSaveAttachments,
  watchGetAttachments,
  watchGetAttachmentUrl,
  watchDeleteAttachment,
  watchAddAttachmentTags,
} from "./attachmentSagas";

import { watchGetNotificationList } from "./notificationSagas";

import {
  watchGetDashboardOverview,
  watchGetPartnerDashboardStats,
} from "./dashboardSagas";

import {
  watchGetAssetContacts,
  watchAddAssetContact,
  watchDeleteAssetContact,
  watchUpdateAssetContact,
  watchGetAllContacts,
  watchAddContacts,
  watchDeleteContacts,
  watchEditContacts,
  watchGetContact,
  watchGetClientAssetsTransactionExcel,
  watchParseBulkAddContactsCsv,
  watchParseBulkAddContactsAssetsCsv,
} from "./contactSagas";

import {
  watchAddFinancialToAsset,
  watchGetAssetFinancials,
  watchUpdateAssetFinancial,
  watchDeleteAssetFinancial,
} from "./financialSagas";

import { watchGetRecommendations } from "./recommendationSagas";
import { watchSendInvite } from "./inviteSaga";
import {
  watchAddDeal,
  watchGetDeals,
  watchGetUserDeals,
  watchTransferAsset,
} from "./dealSagas";
import { watchPropParser } from "./propParserSagas";

import {
  watchGetReminderList,
  watchAddReminder,
  watchUpdateReminder,
} from "./reminderSagas";
import {
  watchGetPartner,
  watchUpdatePartner,
  watchSignupPartner,
  watchListPartners,
} from "./partnerSagas";
import { watchRequestTestimonial } from "./testemonialSagas";
import { watchRequestPermission } from "./permissionSaga";

import {
  watchAddOpportunity,
  watchEditOpportunity,
  watchGetOpportunities,
  watchGetOpportunity,
  watchDeleteOpportunity,
} from "./opportuntiesSagas";

import {
  watchAddImporter,
  watchGetImporters,
  watchDeleteImporter,
  watchImporterParseFile,
} from "./importerSagas";

import {
  watchAddIntegration,
  watchUpdateIntegration,
  watchGetIntegrations,
  watchDeleteIntegration,
  watchSyncIntegration,
} from "./integrationSagas";

export default function* rootSaga() {
  yield all([
    fork(watchLogin),
    fork(watchSignup),
    fork(watchGoogleLogin),
    fork(watchForgotPassword),
    fork(watchResetPassword),
    fork(watchActivateUser),
    fork(watchAssetList),
    // fork(watchGetAssetFromRets),
    fork(watchLookupAsset),
    fork(watchSaveAsset),
    fork(watchAssetDetail),
    fork(watchHasAsset),
    fork(watchGetMonthwiseIncomes),
    fork(watchGetMonthwiseExpenses),
    fork(watchGetMonthlySnapshot),
    fork(watchAddAssetMedias),
    fork(watchGetAssetMedias),
    fork(watchDeleteAssetMedia),
    fork(watchGetDashboardOverview),
    fork(watchGetUserDetail),
    fork(watchUpdateUserDetail),
    fork(watchUpdatePassword),
    fork(watchInvestorProfile),
    fork(watchUpdateInvestorProfile),

    fork(watchSaveFilter),
    fork(watchDeleteAsset),
    fork(watchGetAccounts),
    fork(watchAddAccount),
    fork(watchAddBankToAsset),
    fork(watchRemoveBankFromAsset),
    fork(watchGetTransactions),
    fork(watchRemoveAccount),
    fork(watchUnlinkAssets),
    fork(watchResendEmail),

    fork(watchGetPolicyContent),
    fork(watchSaveFeedback),
    fork(watchGetResources),
    fork(watchGetRecommendations),

    fork(watchGetAttachments),
    fork(watchSaveAttachments),
    fork(watchGetAttachmentUrl),
    fork(watchDeleteAttachment),
    fork(watchAddAttachmentTags),

    fork(watchGetNotificationList),

    fork(watchSaveTransactions),
    fork(watchGetTransactionsFromBank),
    fork(watchAddFinancialToAsset),
    fork(watchGetAssetFinancials),
    fork(watchUpdateAssetFinancial),
    fork(watchDeleteAssetFinancial),
    fork(watchGetAssetContacts),
    fork(watchAddAssetContact),
    fork(watchUpdateAssetContact),
    fork(watchDeleteAssetContact),

    fork(watchGetAssetBasicInfo),
    fork(watchUpdateAssetBasicInfo),

    fork(watchGetAssetInvestmentInfo),
    fork(watchUpdateAssetInvestmentInfo),

    fork(watchGetAccountDetail),
    fork(watchGetAssetExpenses),
    fork(watchGetAssetIncomes),
    fork(watchGetAssetValuation),
    fork(watchGetAssetCashflow),

    fork(watchUpdateTransaction),
    fork(watchDeleteTransaction),
    fork(watchMultiDeleteTransaction),

    fork(watchGetMonthlyOverallCashflow),
    fork(watchGetTotalMonthlyStats),
    fork(watchGetDashboardStats),
    fork(watchGetAssetStats),

    fork(watchSaveAssetNotFoundLog),
    fork(watchExportToExcel),
    fork(watchGetMonthlyGraph),
    fork(watchGetYearlyGraph),
    fork(watchGetCashFlowGraph),
    fork(watchDownloadSampleCSV),
    fork(watchdownloadPastSampleXLSX),
    fork(watchRemoveAllDuplicates),
    fork(watchMarkAllPaid),
    fork(watchGetAllContacts),
    fork(watchSendInvite),
    fork(watchAddDeal),
    fork(watchGetDeals),
    fork(watchReserveResource),
    fork(watchGetUserDeals),
    fork(watchUpdateIncomeGoal),
    fork(watchGetAssetChecklist),
    fork(watchTransferAsset),

    fork(watchGetResourceStats),
    fork(watchPostResource),
    fork(watchUploadFile),

    fork(watchGetMyResources),
    fork(watchGetMyResourceStats),

    fork(watchGetViewResource),
    fork(watchPropParser),
    // fork(watchAddNotification),

    fork(watchGetReminderList),
    fork(watchAddReminder),
    fork(watchUpdateReminder),

    fork(watchGetPartner),
    fork(watchSignupPartner),
    fork(watchUpdatePartner),
    fork(watchListPartners),

    fork(watchBulkAddAsset),
    fork(watchDownloadBulkAddCSV),

    fork(watchAddContacts),
    fork(watchDeleteContacts),
    fork(watchEditContacts),

    fork(watchGetContactAssets),

    fork(watchAddContactAssets),
    fork(watchGetContact),
    fork(watchAddDocument),
    fork(watchSavePartnerTransactions),

    fork(watchDownloadAssetsBulkAddCSV),
    fork(watchBulkAddClient),

    fork(watchGetClientAssetsTransactionExcel),
    fork(watchParseBulkAddContactsCsv),

    fork(watchBulkAddClientAssets),

    fork(watchRequestTestimonial),
    fork(watchParseBulkAddContactsAssetsCsv),

    fork(watchGetPartnerDashboardStats),

    fork(watchManualBulkAddClientAssets),

    fork(watchGetOpportunities),
    fork(watchAddOpportunity),
    fork(watchUploadFiles),

    fork(watchGetOpportunity),

    fork(watchEditOpportunity),
    fork(watchLookupParcel),
    fork(watchAddAssetAndFinancials),
    fork(watchDeleteOpportunity),
    fork(watchRequestPermission),

    fork(watchAddImporter),
    fork(watchGetImporters),
    fork(watchDeleteImporter),
    fork(watchImporterParseFile),

    fork(watchAddIntegration),
    fork(watchUpdateIntegration),
    fork(watchGetIntegrations),
    fork(watchDeleteIntegration),
    fork(watchSyncIntegration),
  ]);
}
